import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { CopyPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PRIORITIES } from "@/lib/colors";
import { vehicleLabel } from "./VehiclesDialog";
import { taskShareText } from "@/lib/share";
import { ShareActions } from "./ShareActions";
import { RecurringHabits } from "./RecurringHabits";
import { SlotPicker } from "./SlotPicker";
import { mondayOf } from "@/lib/dates";
import { parseISO } from "date-fns";

const EMPTY = { title: "", notes: "", time: "", priority: "media", vehicle: "", trailer: false, employee_id: "none", date: "", recurring: false, usual_employee_ids: [], usual_days: [], auto_weekly: false, employee_ids: [], dates: [] };

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-medium text-slate-600">{label}</Label>
    {children}
  </div>
);

export const TaskDialog = ({ state, onClose, employees, vehicles, onSave, onDelete, onCopy, monday }) => {
  const { open, task, preset } = state;
  const [form, setForm] = useState(EMPTY);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const src = task || { ...EMPTY, ...preset };
    setForm({
      ...EMPTY,
      ...src,
      employee_id: src.employee_id || "none",
      date: src.date || "",
      employee_ids: src.employee_id ? [src.employee_id] : [],
      dates: src.date ? [src.date] : [],
    });
    setConfirmDelete(false);
  }, [open, task, preset]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const vehicleOptions = vehicles.map(vehicleLabel);
  if (form.vehicle && !vehicleOptions.includes(form.vehicle)) vehicleOptions.push(form.vehicle);
  const shareText = form.title.trim() ? taskShareText(form, employees.find((e) => e.id === form.employee_id)) : "";

  const multi = !task && !form.recurring;
  const setList = (k) => (fn) => setForm((f) => ({ ...f, [k]: fn(f[k]) }));

  const submit = async (e) => {
    e.preventDefault();
    const employee_id = form.employee_id === "none" || form.recurring || multi ? null : form.employee_id;
    const date = form.recurring || multi ? null : form.date || null;
    if (!form.title.trim()) return toast.error("Inserisci un titolo");
    if (Boolean(employee_id) !== Boolean(date)) return toast.error("Per assegnare scegli sia il dipendente sia il giorno");
    if (multi && Boolean(form.employee_ids.length) !== Boolean(form.dates.length)) return toast.error("Per assegnare scegli almeno un dipendente e un giorno");
    const body = { title: form.title, notes: form.notes, time: form.time, priority: form.priority, vehicle: form.vehicle, trailer: form.trailer, employee_id, date, recurring: form.recurring, usual_employee_ids: form.usual_employee_ids, usual_days: form.usual_days, auto_weekly: form.auto_weekly };
    const slots = multi && form.employee_ids.length ? { employee_ids: form.employee_ids, dates: form.dates } : null;
    if (await onSave(body, task?.id, slots)) onClose();
  };

  const remove = async () => {
    if (!confirmDelete) return setConfirmDelete(true);
    if (await onDelete(task.id)) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="task-dialog" className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{task ? "Modifica attività" : "Nuova attività"}</DialogTitle>
          <DialogDescription>
            {form.recurring ? "Resta tra le ricorrenti: ogni volta che la assegni, nella griglia va una copia." : "Lascia dipendenti e giorni vuoti per metterla tra quelle da assegnare."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="Titolo" className="col-span-2">
            <Input data-testid="task-title-input" maxLength={200} autoFocus value={form.title} onChange={set("title")} placeholder="Es. Consegna magazzino" className="h-11 rounded-xl" />
          </Field>
          <Field label="Orario">
            <Input data-testid="task-time-input" maxLength={120} value={form.time} onChange={set("time")} placeholder="08:30 - 12:00" className="h-11 rounded-xl" />
          </Field>
          <Field label="Priorità">
            <Select value={form.priority} onValueChange={set("priority")}>
              <SelectTrigger data-testid="task-priority-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITIES).map(([k, p]) => (
                  <SelectItem key={k} value={k}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {!task?.date && (
            <label htmlFor="task-recurring" className="col-span-2 flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-xl border border-violet-200 bg-violet-50/60 px-3 text-sm font-medium text-violet-900">
              <Checkbox id="task-recurring" data-testid="task-recurring-checkbox" checked={form.recurring} onCheckedChange={(v) => setForm((f) => ({ ...f, recurring: v === true }))} className="h-5 w-5 rounded-[5px]" />
              Ricorrente (resta nell'elenco anche dopo l'assegnazione)
            </label>
          )}
          {form.recurring && <RecurringHabits form={form} setForm={setForm} employees={employees} />}
          {multi && open && (
            <SlotPicker
              employees={employees}
              empIds={form.employee_ids}
              setEmpIds={setList("employee_ids")}
              dates={form.dates}
              setDates={setList("dates")}
              startMonday={preset?.date ? mondayOf(parseISO(preset.date)) : monday}
              testPrefix="task-slot"
            />
          )}
          {!form.recurring && !multi && (
          <>
          <Field label="Dipendente">
            <Select value={form.employee_id} onValueChange={set("employee_id")}>
              <SelectTrigger data-testid="task-employee-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non assegnata</SelectItem>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Giorno">
            <Input data-testid="task-date-input" type="date" value={form.date} onChange={set("date")} className="h-11 rounded-xl" />
          </Field>
          </>
          )}
          <Field label="Automezzo">
            <Select value={form.vehicle || "none"} onValueChange={(v) => setForm((f) => ({ ...f, vehicle: v === "none" ? "" : v }))}>
              <SelectTrigger data-testid="task-vehicle-select" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Nessuno</SelectItem>
                {vehicleOptions.map((label) => (
                  <SelectItem key={label} value={label}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vehicles.length === 0 && <p className="text-[11px] text-slate-500">Aggiungi i mezzi dal pulsante "Automezzi".</p>}
          </Field>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">&nbsp;</Label>
            <label htmlFor="task-trailer" className="flex h-11 cursor-pointer items-center gap-2.5 rounded-xl border px-3 text-sm font-medium">
              <Checkbox id="task-trailer" data-testid="task-trailer-checkbox" checked={form.trailer} onCheckedChange={(v) => setForm((f) => ({ ...f, trailer: v === true }))} className="h-5 w-5 rounded-[5px]" />
              Rimorchio
            </label>
          </div>
          <Field label="Note" className="col-span-2">
            <Textarea data-testid="task-notes-input" maxLength={2000} value={form.notes} onChange={set("notes")} rows={3} placeholder="Dettagli, indirizzo, riferimenti..." className="rounded-xl" />
          </Field>
          {task && shareText && (
            <div data-testid="task-share-box" className="col-span-2 space-y-2 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
              <p className="text-xs font-medium text-emerald-900">Condividi su WhatsApp</p>
              <pre data-testid="task-share-preview" className="whitespace-pre-wrap break-words font-sans text-xs leading-relaxed text-slate-700">{shareText}</pre>
              <ShareActions text={shareText} testPrefix="task-share" />
            </div>
          )}
          <DialogFooter className="col-span-2 mt-2 flex-row gap-2 sm:justify-between">
            {task ? (
              <Button type="button" data-testid="task-delete-btn" variant="ghost" onClick={remove} className="h-11 gap-2 rounded-xl text-rose-600 hover:bg-rose-50 hover:text-rose-700">
                <Trash2 className="h-4 w-4" /> {confirmDelete ? "Conferma" : "Elimina"}
              </Button>
            ) : <span />}
            {task?.date && (
              <Button type="button" data-testid="task-copy-btn" variant="outline" onClick={() => onCopy(task)} className="ml-auto h-11 gap-2 rounded-xl">
                <CopyPlus className="h-4 w-4" /> Copia
              </Button>
            )}
            <Button type="submit" data-testid="task-save-btn" className={`${task?.date ? "" : "ml-auto"} h-11 rounded-xl px-6`}>Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
